/**
 * 🔄 Реал-тайм синхронизация с глобальным кешем
 * Заменяет TanStack Query интеграцию на работу с Zustand store
 */

import { useEffect, useCallback } from 'react'
import { globalWebSocket } from '../ws/globalWebSocket'
import { useOrdersStore, type Order, type OrderCounts } from './ordersStore'
import { useNotificationsStore } from './notificationsStore'
import { useAuthStore } from '../auth/store'
import { logger } from '../utils/logger'


// 🔒 Дедупликация входящих сообщений, чтобы не инкрементировать дважды
const processedMessageKeys = new Set<string>()

function buildMessageKey(event: NewMessageEvent): string {
  const id = event.message?.id || ''
  const content = event.message?.content || ''
  return `${event.order_id}__${event.timestamp}__${id}__${content.substring(0, 50)}`
}

// 🔔 ФУНКЦИЯ ДЛЯ ПРЯМОГО ПОКАЗА УВЕДОМЛЕНИЙ (КАК ФИОЛЕТОВАЯ ТАБЛИЧКА)
function showChatNotificationDirect(params: {
  chatTitle: string
  messageContent: string
  authorName: string
  orderId: string
}) {

  
  // Воспроизводим звук
  playChatNotificationSound()
  
  // Создаем уведомление прямо через DOM (как фиолетовая табличка)
  const notificationDiv = document.createElement('div')
  const notificationId = `chat-notification-${Date.now()}`
  
  notificationDiv.id = notificationId
  notificationDiv.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    width: 280px;
    max-width: calc(100vw - 40px);
    background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
    color: white;
    padding: 12px;
    border-radius: 8px;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
    z-index: 200; /* Уведомления поверх модальных окон */
    font-family: system-ui, -apple-system, sans-serif;
    animation: slideInFromRight 0.3s ease-out;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
  `
  
  notificationDiv.innerHTML = `
    <div style="display: flex; align-items: center; gap: 10px;">
      <div style="
        width: 32px;
        height: 32px;
        background: rgba(255, 255, 255, 0.2);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      "><ion-icon name="chatbubble" style="font-size: 16px; color: white;"></ion-icon></div>
      
      <div style="flex: 1; min-width: 0;">
        <div style="font-weight: 600; font-size: 13px; margin-bottom: 2px;">
          ${params.chatTitle}
        </div>
        
        <div style="
          font-size: 12px;
          line-height: 1.3;
          opacity: 0.95;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        ">${params.messageContent}</div>
      </div>
      
      <button onclick="document.getElementById('${notificationId}').remove()" style="
        background: none;
        border: none;
        color: white;
        font-size: 16px;
        cursor: pointer;
        padding: 2px;
        opacity: 0.7;
        border-radius: 4px;
        transition: opacity 0.2s;
        flex-shrink: 0;
      " onmouseover="this.style.opacity='1'" onmouseout="this.style.opacity='0.7'">×</button>
    </div>
  `
  
  // Добавляем CSS анимацию
  if (!document.getElementById('chat-notification-styles')) {
    const styleSheet = document.createElement('style')
    styleSheet.id = 'chat-notification-styles'
    styleSheet.textContent = `
      @keyframes slideInFromRight {
        from {
          opacity: 0;
          transform: translateX(100%);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
      
      @keyframes slideOutToRight {
        from {
          opacity: 1;
          transform: translateX(0);
        }
        to {
          opacity: 0;
          transform: translateX(100%);
        }
      }
    `
    document.head.appendChild(styleSheet)
  }
  
  // Уведомление только для информации - не кликабельное
  
  // Добавляем в DOM
  document.body.appendChild(notificationDiv)
  
  // Автоматическое удаление через 3 секунды (компактное уведомление)
  setTimeout(() => {
    if (document.getElementById(notificationId)) {
      notificationDiv.style.animation = 'slideOutToRight 0.3s ease-in'
      setTimeout(() => notificationDiv.remove(), 300)
    }
  }, 3000)
}



// 🔊 УПРАВЛЕНИЕ АУДИО: единый AudioContext + разблокировка по жесту пользователя
let sharedAudioContext: AudioContext | null = null
let audioUnlockInitialized = false

function getAudioContext(): AudioContext {
  if (!sharedAudioContext) {
    sharedAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
  }
  return sharedAudioContext
}

function setupAudioUnlockListeners() {
  if (audioUnlockInitialized) return
  audioUnlockInitialized = true

  const tryResume = () => {
    try {
      const ctx = getAudioContext()
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {})
      }
    } catch {}
  }

  const onGesture = () => {
    tryResume()
    window.removeEventListener('pointerdown', onGesture)
    window.removeEventListener('touchstart', onGesture)
    window.removeEventListener('keydown', onGesture)
  }

  window.addEventListener('pointerdown', onGesture, { passive: true })
  window.addEventListener('touchstart', onGesture, { passive: true })
  window.addEventListener('keydown', onGesture)

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      tryResume()
    }
  })
}

// 🔊 ФУНКЦИЯ ДЛЯ ВОСПРОИЗВЕДЕНИЯ ЗВУКА
function playChatNotificationSound() {

  
  // Проверяем настройку звука в localStorage
  const soundEnabled = localStorage.getItem('chat_sound_enabled') !== 'false'
  if (!soundEnabled) {

    return
  }

  setupAudioUnlockListeners()

  try {
    const audioContext = getAudioContext()
    if (audioContext.state === 'suspended') {
      // Попытка разблокировать перед воспроизведением
      audioContext.resume().catch(() => {})
    }

    const volume = parseFloat(localStorage.getItem('chat_sound_volume') || '0.1')

    const playTone = (frequency: number, duration: number, delayMs: number = 0) => {
      const startAt = audioContext.currentTime + delayMs / 1000
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      oscillator.frequency.setValueAtTime(frequency, startAt)
      oscillator.type = 'sine'

      // Плавное нарастание и затухание
      gainNode.gain.setValueAtTime(0, startAt)
      gainNode.gain.linearRampToValueAtTime(volume, startAt + 0.01)
      gainNode.gain.linearRampToValueAtTime(0.0001, startAt + duration)

      oscillator.start(startAt)
      oscillator.stop(startAt + duration)
    }

    // Приятная мелодия: C5 -> E5
    playTone(523.25, 0.15, 0)    // C5
    playTone(659.25, 0.2, 150)   // E5
  } catch (error) {
    console.warn('Не удалось воспроизвести звук уведомления:', error)
  }
}

// Типы WebSocket событий (из старого файла)
export interface WebSocketEvent {
  type: string
  timestamp: string
  [key: string]: any
}

export interface OrderStatusChangedEvent extends WebSocketEvent {
  type: 'order_status_changed'
  order_id: string
  old_status: string
  new_status: string
  user_id?: string
}

export interface OrdersCountsEvent extends WebSocketEvent {
  type: 'orders_counts'
  counts: OrderCounts
}

export interface NewOrderEvent extends WebSocketEvent {
  type: 'new_order'
  order: Order
}

export interface OrderUpdatedEvent extends WebSocketEvent {
  type: 'order_updated'
  order_id: string
  event: string
}

export interface OfferUpdatedEvent extends WebSocketEvent {
  type: 'offer_updated'
  order_id: string
  offer_id: string
  event: string
}

export interface CrossPlatformSyncEvent extends WebSocketEvent {
  type: 'cross_platform_sync'
  source: 'browser' | 'telegram' | 'manager'
}

export interface ManagerActionEvent extends WebSocketEvent {
  type: 'manager_action'
  order_id: string
  action: string
  new_status?: string
}

export interface OrderViewedEvent extends WebSocketEvent {
  type: 'order_viewed'
  order_id: string
  old_status: string
  new_status: string
  viewer_id?: string
}

export interface NewMessageEvent extends WebSocketEvent {
  type: 'new_message'
  chat_id: string
  chat_title?: string // Заголовок чата для уведомлений
  order_id: string
  offer_id: string
  timestamp: string
  unread_count?: number // Актуальный счетчик непрочитанных сообщений
  message: {
    id: string
    content: string
    author: string
    is_own: boolean
    created_at: string
    files: any[]
    status: string
    message_type: string
  }
}



/**
 * 🔄 Хук для реал-тайм синхронизации с глобальным кешем
 * Заменяет useRealtimeSync из TanStack Query версии
 */
export function useRealtimeSync() {
  const service = globalWebSocket.getService()
  const isConnected = service.getConnectionStatus().isConnected
  const {
    updateOrderInGlobalCache,
    addOrderToGlobalCache,
    updateCountsFromWebSocket,
    recalculateCounts,
    // 🆕 Методы для синхронизации кеша деталей
    updateOrderDetailInCache
  } = useOrdersStore()

  // Обработчик изменения статуса заказа
  const handleOrderStatusChanged = useCallback(async (event: OrderStatusChangedEvent) => {
    // Обновляем конкретный заказ в глобальном кеше
    updateOrderInGlobalCache(event.order_id, {
      status: event.new_status,
      updated_at: event.timestamp,
    })
    
    // 🆕 Синхронизируем кеш деталей заказа
    updateOrderDetailInCache(event.order_id, {
      status: event.new_status,
      updated_at: event.timestamp,
    })
    
    // 🆕 Загружаем дедлайн если статус изменился на executing
    if (event.new_status === 'executing') {
      try {
        const { getOrderDeadline } = await import('../api/deadlineApi')
        const deadlineResponse = await getOrderDeadline(event.order_id)
        
        if (deadlineResponse.success && deadlineResponse.deadline) {
          const deadlineData = {
            completion_deadline_formatted: deadlineResponse.deadline.completion_deadline_formatted,
            deadline_status: deadlineResponse.deadline.status_text,
            deadline_css_class: deadlineResponse.deadline.css_class,
            days_remaining: deadlineResponse.deadline.days_remaining,
            confirmed_at: deadlineResponse.deadline.confirmed_at,
            delivery_days: deadlineResponse.deadline.delivery_days
          }
          
          // Обновляем кеши с информацией о дедлайне
          updateOrderInGlobalCache(event.order_id, deadlineData)
          updateOrderDetailInCache(event.order_id, deadlineData)
          
          logger.log(`📅 WebSocket: Дедлайн загружен для заказа #${event.order_id}: ${deadlineResponse.deadline.completion_deadline_formatted}`)
        }
      } catch (error) {
        logger.error(`❌ Ошибка загрузки дедлайна для заказа #${event.order_id}:`, error)
      }
    }
    
    logger.log(`🔄 WebSocket: Статус заказа #${event.order_id} обновлен до "${event.new_status}" в обоих кешах`)
  }, [updateOrderInGlobalCache, updateOrderDetailInCache])

  // Обработчик обновления счетчиков
  const handleOrdersCountsUpdate = useCallback((event: OrdersCountsEvent) => {
    // Обновляем счетчики в глобальном кеше
    updateCountsFromWebSocket(event.counts)
  }, [updateCountsFromWebSocket])

  // Обработчик нового заказа
  const handleNewOrder = useCallback((event: NewOrderEvent) => {
    // Добавляем новый заказ в глобальный кеш
    addOrderToGlobalCache(event.order)
  }, [addOrderToGlobalCache])

  // Обработчик обновления заказа
  const handleOrderUpdated = useCallback((event: OrderUpdatedEvent) => {
    // Для общих обновлений просто пересчитываем счетчики
    // Конкретные изменения придут через order_status_changed
    recalculateCounts()
  }, [recalculateCounts])

  // Обработчик обновления ценового предложения
  const handleOfferUpdated = useCallback((event: OfferUpdatedEvent) => {
    const timestamp = new Date().toISOString()
    
    // Обновляем заказ (возможно изменилась цена)
    updateOrderInGlobalCache(event.order_id, {
      updated_at: timestamp
    })
    
    // 🆕 Синхронизируем кеш деталей заказа
    updateOrderDetailInCache(event.order_id, {
      updated_at: timestamp
    })
    
    logger.log(`🔄 WebSocket: Предложение для заказа #${event.order_id} обновлено в обоих кешах`)
  }, [updateOrderInGlobalCache, updateOrderDetailInCache])

  // Обработчик кроссплатформенной синхронизации
  const handleCrossPlatformSync = useCallback((event: CrossPlatformSyncEvent) => {
    // При кроссплатформенной синхронизации пересчитываем счетчики
    recalculateCounts()
  }, [recalculateCounts])

  // Обработчик действий менеджера
  const handleManagerAction = useCallback((event: ManagerActionEvent) => {
    // Обновляем заказ если известен новый статус
    const updates: Partial<Order> = {
      updated_at: new Date().toISOString()
    }

    if (event.new_status) {
      updates.status = event.new_status
    }

    updateOrderInGlobalCache(event.order_id, updates)
    
    // 🆕 Синхронизируем кеш деталей заказа
    updateOrderDetailInCache(event.order_id, updates)
    
    logger.log(`🔄 WebSocket: Действие менеджера для заказа #${event.order_id} синхронизировано в обоих кешах`)
  }, [updateOrderInGlobalCache, updateOrderDetailInCache])

  // Обработчик просмотра заказа
  const handleOrderViewed = useCallback((event: OrderViewedEvent) => {
    // Обновляем статус заказа в глобальном кеше
    updateOrderInGlobalCache(event.order_id, {
      status: event.new_status,
      updated_at: event.timestamp,
    })
    
    // 🆕 Синхронизируем кеш деталей заказа
    updateOrderDetailInCache(event.order_id, {
      status: event.new_status,
      updated_at: event.timestamp,
    })
    
    logger.log(`🔄 WebSocket: Просмотр заказа #${event.order_id} синхронизирован в обоих кешах`)
  }, [updateOrderInGlobalCache, updateOrderDetailInCache])

  // Обработчик нового сообщения в чате
  const handleNewMessage = useCallback((event: NewMessageEvent) => {


    
    // 🔔 ПОКАЗЫВАЕМ УВЕДОМЛЕНИЕ ПРИ ПОЛУЧЕНИИ НОВОГО СООБЩЕНИЯ (ПРЯМО ЧЕРЕЗ DOM)
    // Проверяем что это не собственное сообщение
    if (!event.message?.is_own && event.message?.content) {
      // Получаем название заказа из глобального кеша или используем переданный chat_title
      const { allOrders } = useOrdersStore.getState()
      const order = allOrders.find(o => String(o.id) === String(event.order_id))
      const orderTitle = event.chat_title || order?.title || `Заказ #${event.order_id}`
      
      // Отладка для проверки chat_title
      console.log('🔍 [REALTIME_SYNC] Обработка new_message:', {
        event_chat_title: event.chat_title,
        order_title: order?.title,
        final_orderTitle: orderTitle,
        order_id: event.order_id
      })
      
      showChatNotificationDirect({
        chatTitle: orderTitle, // Используем chat_title из события или реальное название заказа
        messageContent: event.message?.content || '',
        authorName: event.message?.author || 'Менеджер',
        orderId: event.order_id
      })
    }
    
    // 🔢 Обновляем глобальный счетчик (с дедупликацией), если это не наше сообщение
    // и если чат не открыт сейчас
    if (!event.message?.is_own) {
      const key = buildMessageKey(event)
      if (!processedMessageKeys.has(key)) {
        processedMessageKeys.add(key)
        // Очищаем ключ через 10 минут, чтобы не расти бесконечно
        setTimeout(() => processedMessageKeys.delete(key), 10 * 60 * 1000)

        const { counts, updateUnreadMessagesCount, activeChatId } = useOrdersStore.getState()
        const currentActive = activeChatId
        const isSameChatOpen = currentActive === event.chat_id || (currentActive && currentActive.startsWith('chat_') && currentActive === `chat_${event.order_id}`)

        if (!isSameChatOpen) {
          updateUnreadMessagesCount((counts.unread_messages || 0) + 1)
        }
      }
    }

    
    // Обновляем время последнего обновления заказа в глобальном кеше
    updateOrderInGlobalCache(event.order_id, {
      updated_at: event.timestamp,
      // Добавляем индикатор нового сообщения
      has_new_messages: true,
    })
    
    // 🆕 Синхронизируем кеш деталей заказа
    updateOrderDetailInCache(event.order_id, {
      updated_at: event.timestamp,
      has_new_messages: true,
    })
    
    // 🔔 Счетчики уведомлений теперь управляются через ordersStore
    // Дополнительная логика не нужна
    
    // 🚨 Локальные обработчики чата получат событие автоматически
    // через тот же WebSocket поток, дополнительные эмиты не нужны
    
    logger.log(`💬 WebSocket: Новое сообщение для заказа #${event.order_id} - кеши и уведомления обновлены`)
  }, [updateOrderInGlobalCache, updateOrderDetailInCache])

  // Обработчик уведомлений колокольчика
  const handleNotification = useCallback((event: any) => {
    
    // Добавляем уведомление в колокольчик
    if (event.data && event.data.type === 'notification') {
      const notification = event.data
      useNotificationsStore.getState().addNotification(notification)
      
      // Показываем визуальное уведомление
      if (notification.title && notification.message) {
        showChatNotificationDirect({
          chatTitle: notification.title,
          messageContent: notification.message,
          authorName: 'Система',
          orderId: notification.order_id || ''
        })
      }
    }
    
    // Обрабатываем прямые уведомления (без вложенности в data)
    if (event.type === 'notification' && event.id && event.title && event.message) {
      const notification = {
        id: event.id,
        type: event.notification_type || event.category || 'system',
        title: event.title,
        message: event.message,
        order_id: event.order_id,
        offer_id: event.offer_id,
        related_data: event.related_data,
        is_read: false,
        priority: event.priority || 'normal',
        category: event.category,
        created_at: event.created_at || new Date().toISOString()
      }
      
      useNotificationsStore.getState().addNotification(notification)
      
      // Показываем визуальное уведомление для отмены просчета
      if (event.category === 'calc_cancel' || event.notification_type === 'calc_cancel') {
        showChatNotificationDirect({
          chatTitle: '💡 ' + notification.title,
          messageContent: notification.message,
          authorName: 'Система',
          orderId: notification.order_id || ''
        })
      } else if (notification.title && notification.message) {
        showChatNotificationDirect({
          chatTitle: notification.title,
          messageContent: notification.message,
          authorName: 'Система',
          orderId: notification.order_id || ''
        })
      }
    }
  }, [])

  // 🔧 ВРЕМЕННЫЙ ОБРАБОТЧИК: Для сообщений чата, пока backend не исправлен
  const handleChatMessage = useCallback((event: any) => {
    
    // Проверяем, это сообщение чата
    if (event.type === 'chat_message' || (event.message && event.message.content)) {
      // Обновляем счетчик чата
      const { counts, updateUnreadMessagesCount } = useOrdersStore.getState()
      const currentCount = counts.unread_messages || 0
      updateUnreadMessagesCount(currentCount + 1)
      

    }
  }, [])

  // Подписка на WebSocket события
  useEffect(() => {

    
    if (!service) {

      return
    }
    
    // ⚠️ ИСПРАВЛЕНИЕ: Регистрируем обработчики даже если isConnected=false
    // WebSocket может быть подключен, но состояние isConnected может не обновляться в Telegram
    if (!isConnected) {

    }



    // Регистрируем обработчики событий
    service.on('order_status_changed', handleOrderStatusChanged)
    service.on('orders_counts', handleOrdersCountsUpdate)
    service.on('new_order', handleNewOrder)
    service.on('order_updated', handleOrderUpdated)
    service.on('offer_updated', handleOfferUpdated)
    service.on('cross_platform_sync', handleCrossPlatformSync)
    service.on('manager_action', handleManagerAction)
    service.on('order_viewed', handleOrderViewed)
    service.on('new_message', handleNewMessage)
    
    // ⚠️ ИСПРАВЛЕНИЕ: Добавляем обработчик для типа 'notification'
    service.on('notification', handleNotification)
    
    // 🔧 ИСПРАВЛЕНИЕ: Добавляем альтернативный обработчик для сообщений чата
    service.on('chat_message', handleChatMessage)

    // Очистка при размонтировании
    return () => {
      service.off('order_status_changed', handleOrderStatusChanged)
      service.off('orders_counts', handleOrdersCountsUpdate)
      service.off('new_order', handleNewOrder)
      service.off('order_updated', handleOrderUpdated)
      service.off('offer_updated', handleOfferUpdated)
      service.off('cross_platform_sync', handleCrossPlatformSync)
      service.off('manager_action', handleManagerAction)
      service.off('order_viewed', handleOrderViewed)
      service.off('new_message', handleNewMessage)
      service.off('notification', handleNotification)
      service.off('chat_message', handleChatMessage)
    }
  }, [
    service,
    // isConnected, // ⚠️ УБИРАЕМ зависимость от isConnected
    handleOrderStatusChanged,
    handleOrdersCountsUpdate,
    handleNewOrder,
    handleOrderUpdated,
    handleOfferUpdated,
    handleCrossPlatformSync,
    handleManagerAction,
    handleOrderViewed,
    handleNewMessage,
    handleNotification,
    handleChatMessage
  ])

  return {
    isConnected,
    // Можно добавить дополнительные методы если нужно
  }
}

/**
 * 🌐 Хук для кроссплатформенной синхронизации
 */
export function useCrossPlatformSync() {
  const { recalculateCounts } = useOrdersStore()

  // Синхронизация при фокусе окна (когда пользователь переключается между платформами)
  useEffect(() => {
    const handleFocus = () => {
      recalculateCounts()
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        recalculateCounts()
      }
    }

    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [recalculateCounts])

  return {
    // Можно добавить методы для ручной синхронизации
    forceSync: recalculateCounts
  }
}

/**
 * 🔄 Главный хук реал-тайм системы для глобального кеша
 * Заменяет useRealtimeSystem из TanStack Query версии
 */
export function useRealtimeSystem() {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated())
  const service = globalWebSocket.getService()
  const connectionInfo = service.getConnectionStatus()
  const isConnected = connectionInfo.isConnected
  
  // Инициализируем реал-тайм синхронизацию только для авторизованных пользователей
  const realtimeSync = useRealtimeSync()
  const crossPlatformSync = useCrossPlatformSync()

  // Определяем платформу (из старого кода)
  const isTelegramMode = !!(window as any).Telegram?.WebApp?.initDataUnsafe?.user?.id
  const platform = isTelegramMode ? 'telegram' : 'browser'

  // Фоновая синхронизация активна если не подключен WebSocket но есть авторизация
  const isBackgroundSyncActive = !isConnected && isAuthenticated && connectionInfo.readyState !== WebSocket.CONNECTING

  return {
    isConnected,
    connectionStatus: connectionInfo.readyState === WebSocket.OPEN ? 'connected' : 'disconnected',
    isBackgroundSyncActive,
    platform,
    isTelegramMode,
    lastSyncTime: new Date().toISOString(), // Заглушка, можно добавить реальное время
    forceSync: crossPlatformSync.forceSync,
    // Отладочная информация
    debugInfo: {
      connectionStatus: connectionInfo.readyState === WebSocket.OPEN ? 'connected' : 'disconnected',
      isAuthenticated,
      isConnected
    }
  }
}
